import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { defaultProfileImage } from "../../../utils/constants";

/**
 * Card de una SOLICITUD en /my-expertise (ExpertiseRequest).
 *
 * Ojo con la diferencia respecto a ExpertiseCard del panel admin: alli la card describe un
 * SERVICIO y muestra al experto; aqui describe lo que un miembro le PIDIO, asi que quien aparece
 * en la foto es el solicitante. La estructura y las clases son las mismas a proposito
 * (`expertise-list-item` y todo lo que cuelga de ella) porque comparten diseno.
 */
type Props = {
    item: any;
    onAccept?: (item: any) => void;
    onReschedule?: (item: any) => void;
    onReject?: (item: any) => void;
}


export const ExpertiseItemCard = ({ item, onAccept, onReschedule, onReject }: Props) => {
    const [expanded, setExpanded] = useState(false);
    // Solo se muestra READ MORE si el texto de verdad se corta: en un mensaje de dos lineas el
    // boton sobra y confunde, porque al pulsarlo no pasa nada visible.
    const [isTruncated, setIsTruncated] = useState(false);
    const descriptionRef = useRef<HTMLSpanElement>(null);

    const measure = useCallback(() => {
        const el = descriptionRef.current;
        if (!el) return;
        setIsTruncated(el.scrollHeight > el.clientHeight + 1);
    }, []);

    useLayoutEffect(() => {
        if (!expanded) measure();
    }, [expanded, item?.message, measure]);

    useEffect(() => {
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [measure]);

    const requester = item?.requestedBy;
    const service = item?.expertise;
    // Lo calcula el backend: el plazo NO es la fecha agendada, sino esa fecha menos el colchon de
    // 5 dias. Reproducir la resta aqui daria un numero distinto en cuanto cambie el colchon.
    const remaining: number | null = item?.daysLeftToRespond ?? null;
    const hasActions = Boolean(onAccept || onReschedule || onReject);
    // El serializer del back (ExpertiseRequestSerializer.get_conversation_uuid) siempre acompana la
    // solicitud con el uuid del hilo abierto en /messages — asi cada card puede saltar al chat sin
    // que la pagina tenga que cruzarlo contra la lista de conversaciones. axios no camelize, por eso
    // aceptamos ambos formatos: si el back gana un pase de camelize en el futuro, sigue funcionando.
    const conversationUuid: string | null =
        item?.conversationUuid ?? item?.conversation_uuid ?? null;

    return (
        <div className="expertise-list-item">
            <div className="profile">
                <div className="quote">
                    <span>{requester?.name ?? 'Member'}</span>
                </div>
                <img src={requester?.avatar ?? defaultProfileImage} alt={requester?.name ?? 'Member'} />
            </div>
            <div className="info">
                <div className="d-flex flex-row justify-content-between w-100">
                    <div className="rate-card">
                        <span className="label">Rate</span>
                        <span className="value">${Number(service?.rate ?? 0)}/hour</span>
                    </div>
                    <div className="d-flex flex-column align-items-end">
                        {item?.scheduledOn && (
                            <div className="schedule-info">
                                <span className="value">{dayjs(item.scheduledOn).format('D MMMM YYYY')}</span>
                            </div>
                        )}
                        {remaining !== null && (
                            <span className="expertise-deadline">
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                                    <circle cx="7" cy="7" r="6" stroke="#808080" />
                                    <path d="M7 3.8V7l2.1 1.3" stroke="#808080" strokeLinecap="round" />
                                </svg>
                                {remaining === 0
                                    ? 'Last day to accept'
                                    : `${remaining} ${remaining === 1 ? 'day' : 'days'} left to accept`}
                            </span>
                        )}
                    </div>
                </div>
                <div className="info-box">
                    <div className="d-flex flex-row w-100">
                        <span className="label" style={{ width: '109px' }}>EXPERTISE</span>
                        <span className="value">{service?.title}</span>
                    </div>

                    <svg xmlns="http://www.w3.org/2000/svg" width="417" height="1" viewBox="0 0 417 1" fill="none">
                        <path d="M0 0.5H417" stroke="#E9E9E9"/>
                    </svg>
                    <div className="d-flex flex-row w-100">
                        <span className="label" style={{ width: '109px' }}>MESSAGE</span>
                        <div className="content" style={{ flex: '1 0 0' }}>
                            <span
                                ref={descriptionRef}
                                className={`value${expanded ? '' : ' value--clamped'}`}
                            >
                                {item?.message}
                            </span>
                            {(isTruncated || expanded) && (
                                <button
                                    type="button"
                                    className="read-more-btn"
                                    onClick={() => setExpanded((prev) => !prev)}
                                    aria-expanded={expanded}
                                >
                                    {expanded ? 'READ LESS' : 'READ MORE'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Reagenda viva: el turno es de quien NO propuso. Se avisa para que el experto no
                    crea que la solicitud sigue esperando su respuesta cuando ya respondio. */}
                {item?.proposedOn && (
                    <div className="expertise-decline">
                        <strong>Reschedule proposed.</strong>{' '}
                        {dayjs(item.proposedOn).format('D MMMM YYYY - h:mma')} — waiting for{' '}
                        {item?.proposedBy?.id === requester?.id ? 'your' : 'their'} confirmation.
                    </div>
                )}

                {/* Fila inferior: en la izquierda REJECT + SEND MESSAGE (el link va PEGADO al
                    boton de Reject, no arrancado); en la derecha RESCHEDULE + ACCEPT. El link
                    solo se pinta cuando ya existe hilo (conversationUuid) — sin uuid no tiene
                    sentido mandar a /messages porque no hay conversacion todavia. Los botones
                    Accept/Reschedule/Reject solo aplican en la pestana "Request". */}
                {(hasActions || conversationUuid) && (
                    <div className="d-flex flex-row justify-content-between align-items-center w-100">
                        <div className="d-flex flex-row align-items-center">
                            {onReject && (
                                <button type="button" className="expertise-btn expertise-btn--reject" onClick={() => onReject(item)}>
                                    Reject
                                </button>
                            )}
                            {conversationUuid && (
                                <Link
                                    to={`/messages?uuid=${conversationUuid}`}
                                    className="expertise-open-chat"
                                >
                                    Send message
                                </Link>
                            )}
                        </div>
                        <div className="d-flex flex-row align-items-center">
                            {onReschedule && (
                                <button type="button" className="expertise-btn expertise-btn--ghost" onClick={() => onReschedule(item)}>
                                    Reschedule
                                </button>
                            )}
                            {onAccept && (
                                <button type="button" className="expertise-btn expertise-btn--accept" onClick={() => onAccept(item)}>
                                    <span>Accept</span>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="4" viewBox="0 0 14 4" fill="none">
                                        <path d="M8.5 0.382812L12 3.38696H0" stroke="white"/>
                                    </svg>
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default ExpertiseItemCard;
